import { useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const SERVICES = ["Wealth Management", "Mutual Fund Advisory", "Financial Planning", "Corporate Advisory", "Tax Planning", "Other"];

function nextBusinessDays(count = 10) {
  const out = [];
  const d = new Date();
  d.setHours(0,0,0,0);
  while (out.length < count) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0) out.push(new Date(d));
  }
  return out;
}
const SLOTS = ["10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00"];

export default function Book() {
  const { user } = useAuth();
  const days = useMemo(() => nextBusinessDays(12), []);
  const [selectedDay, setSelectedDay] = useState(days[0]);
  const [slot, setSlot] = useState(SLOTS[0]);
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "", service: SERVICES[0], notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const [h, m] = slot.split(":").map(Number);
      const dt = new Date(selectedDay); dt.setHours(h, m, 0, 0);
      const iso = dt.toISOString();
      const { data } = await api.post("/appointments", { ...form, slot: iso });
      setDone(data);
      toast.success("Consultation requested — check your inbox.");
    } catch (err) { toast.error(formatErr(err)); } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="overline text-gold-dark mb-4">Confirmed</div>
        <h1 className="font-display text-4xl text-midnight mb-4">Thank you, {done.name.split(" ")[0]}.</h1>
        <p className="text-midnight/70">Your consultation for <b>{done.service}</b> on <b>{new Date(done.slot).toLocaleString()}</b> is pending confirmation. We'll email you shortly.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12">
      <div>
        <div className="overline text-gold-dark mb-6">Book a consultation</div>
        <h1 className="font-display text-5xl md:text-6xl text-midnight tracking-tight">30 minutes,<br /><span className="italic">on us</span>.</h1>
        <p className="mt-6 text-midnight/70 max-w-md leading-relaxed">A discovery call to understand your situation and see if we're the right fit. No obligation, no product pitch.</p>
        <img src="https://images.pexels.com/photos/8441820/pexels-photo-8441820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="mt-10 rounded-2xl aspect-[4/3] w-full object-cover" />
      </div>

      <form onSubmit={submit} data-testid="book-form" className="bg-white rounded-2xl p-8 border border-black/5 space-y-5">
        <div>
          <label className="overline text-midnight/70">Pick a day</label>
          <div className="mt-3 grid grid-cols-4 md:grid-cols-6 gap-2">
            {days.map((d) => {
              const active = d.toDateString() === selectedDay.toDateString();
              return (
                <button type="button" key={d.toISOString()} data-testid={`book-day-${d.getDate()}`} onClick={()=>setSelectedDay(d)} className={`p-2 rounded-lg text-xs border transition-colors ${active ? "bg-midnight text-white border-midnight" : "border-midnight/15 hover:border-gold"}`}>
                  <div className="text-[10px] opacity-70">{d.toLocaleDateString("en-IN",{weekday:"short"})}</div>
                  <div className="font-display text-lg">{d.getDate()}</div>
                  <div className="text-[10px] opacity-70">{d.toLocaleDateString("en-IN",{month:"short"})}</div>
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="overline text-midnight/70">Pick a time</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {SLOTS.map((s) => (
              <button type="button" key={s} data-testid={`book-slot-${s}`} onClick={()=>setSlot(s)} className={`px-4 py-2 rounded-full text-sm border transition-colors ${slot===s ? "bg-gold border-gold text-white" : "border-midnight/15 hover:border-gold"}`}>{s}</button>
            ))}
          </div>
        </div>
        <input required placeholder="Full name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="book-name" className="w-full px-4 py-3 rounded-lg border border-midnight/15 focus:border-gold outline-none text-sm" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="book-email" className="w-full px-4 py-3 rounded-lg border border-midnight/15 focus:border-gold outline-none text-sm" />
        <input required placeholder="Phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="book-phone" className="w-full px-4 py-3 rounded-lg border border-midnight/15 focus:border-gold outline-none text-sm" />
        <select value={form.service} onChange={(e)=>setForm({...form,service:e.target.value})} data-testid="book-service" className="w-full px-4 py-3 rounded-lg border border-midnight/15 focus:border-gold outline-none text-sm bg-white">
          {SERVICES.map((s)=><option key={s}>{s}</option>)}
        </select>
        <textarea rows={3} placeholder="Anything we should know?" value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} data-testid="book-notes" className="w-full px-4 py-3 rounded-lg border border-midnight/15 focus:border-gold outline-none text-sm resize-none" />
        <button type="submit" disabled={loading} data-testid="book-submit" className="btn-gold w-full">{loading ? "Booking…" : "Confirm consultation"}</button>
      </form>
    </div>
  );
}
